import { useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useScraperStore } from '@/stores/useScraperStore';

export function useScraperUrlSync() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const store = useScraperStore();

  // Sync URL to store on initial load
  useEffect(() => {
    // Get current filter values from URL
    const searchTerm = searchParams?.get('q') || '';
    const sortBy = searchParams?.get('sort') || 'recommended';

    const sourcePlatformsParam = searchParams?.get('platforms');
    const sourcePlatforms = sourcePlatformsParam ? sourcePlatformsParam.split(',') : [];

    const serviceType = searchParams?.get('type') || undefined;

    const priceMin = searchParams?.get('priceMin')
      ? parseFloat(searchParams.get('priceMin')!)
      : undefined;
    const priceMax = searchParams?.get('priceMax')
      ? parseFloat(searchParams.get('priceMax')!)
      : undefined;

    const deliveryTime = searchParams?.get('delivery') || undefined;
    const providerRating = searchParams?.get('rating')
      ? parseFloat(searchParams.get('rating')!)
      : undefined;

    const page = searchParams?.get('page') ? parseInt(searchParams.get('page')!) : 1;

    // Update store with URL values
    store.setSearchTerm(searchTerm);
    store.setSortBy(sortBy);
    store.setSourcePlatforms(sourcePlatforms);
    store.setServiceType(serviceType);
    store.setPriceRange({ min: priceMin, max: priceMax });
    store.setDeliveryTime(deliveryTime);
    store.setProviderRating(providerRating);
    store.setCurrentPage(page);
  }, [searchParams]);

  // Sync store to URL when store changes
  useEffect(() => {
    const handleStoreChange = () => {
      const state = useScraperStore.getState();
      const params = new URLSearchParams(searchParams?.toString() || '');

      // Update URL params based on store state
      if (state.searchTerm) params.set('q', state.searchTerm);
      else params.delete('q');

      if (state.sortBy) params.set('sort', state.sortBy);
      else params.delete('sort');

      if (state.sourcePlatforms && state.sourcePlatforms.length > 0)
        params.set('platforms', state.sourcePlatforms.join(','));
      else params.delete('platforms');

      if (state.serviceType) params.set('type', state.serviceType);
      else params.delete('type');

      if (state.priceRange.min !== undefined)
        params.set('priceMin', state.priceRange.min.toString());
      else params.delete('priceMin');

      if (state.priceRange.max !== undefined)
        params.set('priceMax', state.priceRange.max.toString());
      else params.delete('priceMax');

      if (state.deliveryTime) params.set('delivery', state.deliveryTime);
      else params.delete('delivery');

      if (state.providerRating !== undefined) params.set('rating', state.providerRating.toString());
      else params.delete('rating');

      if (state.currentPage > 1) params.set('page', state.currentPage.toString());
      else params.delete('page');

      // Update URL without causing a page reload
      router.push(`?${params.toString()}`, { scroll: false });
    };

    // Subscribe to store changes
    const unsubscribe = useScraperStore.subscribe(handleStoreChange);

    // Cleanup subscription
    return () => unsubscribe();
  }, [router, searchParams]);
}
